import React, { useState, useContext, useEffect, useRef } from "react";
import { gql, useLazyQuery, useApolloClient } from "@apollo/client";
import { Route, Switch, Redirect } from "react-router-dom";
import { withRouter } from "react-router";
import { useAuth } from "./contexts/AuthContext";
import { hasAnyRole, getDefaultRoute, isTechnician, isAdministrator, isCustomer, TECHNICIAN_HOME } from "./utils/roles";
import Login from "./components/Login";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import FirstLoginWelcome from "./components/FirstLoginWelcome";
import {
  LoadingView,
  AppNavigation,
  NavigationMenu,
  RouteStack,
  NotificationManager,
  BasicHeader
} from "@solsys/solsys-reactjs";
import { FaHome, FaTrello, FaCogs, FaCog, FaLayerGroup, FaBoxes, FaUsers, FaEnvelope, FaRobot } from "react-icons/fa";
import { TbCrane, TbBrandGoogleMaps, TbTag, TbTicket, TbBellRinging2, TbRefresh } from "react-icons/tb";
import { RiUserSearchFill } from "react-icons/ri";
import { DateTime } from "luxon";
import ProspectController from "./components/prospect/ProspectController";
import CraneController from "./components/crane/CraneController";
import TypeIndicatorController from "./components/typeIndicator/TypeIndicatorController";
import LocationIndicatorController from "./components/locationIndicator/LocationIndicatorController";
import RegionController from "./components/region/RegionController";
import CustomerController from "./components/customer/CustomerController";
import CustomerHome from "./components/customer/CustomerHome";
import MultiView from "./components/mapview/MultiView";
import Dashboard from "./components/dashboard/Dashboard";
import ManagementDashboard from "./components/dashboard/ManagementDashboard";
import { UserListProvider } from "./contexts/UserListContext";
import "semantic-ui-css/semantic.min.css";
import "@solsys/solsys-reactjs/dist/assets/css/Global.css";
import "./Styles/App.css";
import TagController from "./components/tag/TagController";
import TicketController from "./components/ticket/TicketController";
import ServiceDefinitionController from "./components/serviceDefinition/ServiceDefinitionController";
import ServiceSectionController from "./components/serviceSection/ServiceSectionController";
import ServiceTypeController from "./components/serviceType/ServiceTypeController";
import ServiceUnitController from "./components/serviceUnit/ServiceUnitController";
import ServiceGroupController from "./components/serviceGroup/ServcieGroupController";
import ServiceItemController from "./components/serviceItem/ServiceItemController";
import ServiceController from "./components/service/ServiceController";
import FSRController from "./components/fsr/FSRController";
import PartController from "./components/part/PartController";
import UserController from "./components/user/UserController";
import NotificationEmailsSettings from "./components/settings/NotificationEmailsSettings";
import PushNotificationsSettings from "./components/settings/PushNotificationsSettings";
import McpSettings from "./components/settings/McpSettings";
import { CRANE_EVENT_TYPE, PROSPECT_EVENT_TYPE } from "./eventTypes";
import {
  getBrowserPushSubscription,
  getServiceWorkerRegistration,
  isPushSupported,
  parsePushSubscription,
  useUpdateSubscriptions,
} from "./subscriptionService";

const THEME_KEY = "ui-theme";

function getInitialTheme() {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === "dark" || stored === "light") return stored;
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

const notificationQuery = gql`
  query eventCheckQuery(
    $prospect_event_check_list_request: prospect_event_check_list_request
    $crane_event_check_list_request: crane_event_check_list_request
  ) {
    prospect_event_check(prospect_event_check_list_request: $prospect_event_check_list_request) {
      edges {
        id
        read_status
        prospect_event {
          id
          message
          user_display_name
          created_at
          prospect_id
          type_id
        }
      }
    }
    crane_event_check(crane_event_check_list_request: $crane_event_check_list_request) {
      edges {
        id
        read_status
        crane_event {
          id
          message
          user_display_name
          created_at
          crane_id
          type_id
        }
      }
    }
  }
`;

const eventCheckMutation = gql`
  mutation prospectEventCheckMutate($prospect_event_check_mutation_request: MutationInput) {
    prospect_event_check(prospect_event_check_mutation_request: $prospect_event_check_mutation_request) {
      id
      isNew
    }
  }
`;

function App({ history }) {
  const { user, loading: authLoading, logout, dismissFirstLogin } = useAuth();
  const [theme, setTheme] = useState(getInitialTheme);
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(!menuOpen);
  const [subscription, setSubscription] = useState('');
  const { addPushSubscription, ensurePushSubscription } = useUpdateSubscriptions();
  const [notifications, setNotifications] = useState([]);
  const [notifyCount, setNotifyCount] = useState(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayOption, setDisplayOption] = useState(true);
  const notificationRef = useRef(null);
  const bellRef = useRef(null);
  const client = useApolloClient();
  const isHandlingUnauthorizedRef = useRef(false);
  const isDarkMode = theme === "dark";

  const toggleNotification = () => setIsNotificationOpen(!isNotificationOpen);
  const handleOptionChange = () => setDisplayOption(!displayOption);
  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const response = await nativeFetch(...args);
      if (response?.status === 401 && !isHandlingUnauthorizedRef.current) {
        const path = window.location.pathname;
        const isAuthRoute =
          path.startsWith("/login") ||
          path.startsWith("/forgot-password") ||
          path.startsWith("/reset-password");
        if (!isAuthRoute) {
          isHandlingUnauthorizedRef.current = true;
          try {
            await logout();
            history.replace("/login");
          } finally {
            isHandlingUnauthorizedRef.current = false;
          }
        }
      }
      return response;
    };
    return () => {
      window.fetch = nativeFetch;
    };
  }, [history, logout]);

  const syncBrowserSubscriptionToState = async () => {
    if (!isPushSupported()) {
      setSubscription("");
      return null;
    }
    try {
      const registration = await getServiceWorkerRegistration();
      const browserSub = await getBrowserPushSubscription(registration);
      const parsed = parsePushSubscription(browserSub);
      if (parsed?.raw) {
        setSubscription(parsed.raw);
        return parsed;
      }
      setSubscription("");
      return null;
    } catch (error) {
      console.error("Error reading push subscription:", error);
      setSubscription("");
      return null;
    }
  };

  const registerServiceWorker = async () => {
    if (!isPushSupported()) {
      return;
    }

    try {
      // Register SW; only auto-subscribe when permission is already granted.
      // Requesting permission here (especially on iOS) needs a user gesture —
      // that path lives on Manage Notifications.
      await getServiceWorkerRegistration();
      if (Notification.permission === "granted") {
        const result = await ensurePushSubscription(user?._id, {
          forceFresh: false,
          requestPermission: false,
        });
        if (result.subscription?.raw) {
          setSubscription(result.subscription.raw);
        } else {
          await syncBrowserSubscriptionToState();
        }
      } else {
        await syncBrowserSubscriptionToState();
      }
    } catch (error) {
      console.error("Error registering service worker:", error);
    }
  };

  useEffect(() => {
    registerServiceWorker();
  }, []);

  // Once the user session is available, sync/create the server registration.
  useEffect(() => {
    if (!user?._id || !isPushSupported()) return;
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

    ensurePushSubscription(user._id, { forceFresh: false, requestPermission: false })
      .then((result) => {
        if (result.subscription?.raw) setSubscription(result.subscription.raw);
      })
      .catch((err) => console.error(err));
  }, [user?._id]);

  // Re-sync when returning to the app (covers iOS 410 drop-offs while backgrounded).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && user?._id && Notification.permission === "granted") {
        ensurePushSubscription(user._id, { forceFresh: false, requestPermission: false })
          .then((result) => {
            if (result.subscription?.raw) setSubscription(result.subscription.raw);
          })
          .catch((err) => console.error(err));
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [user?._id]);

  const signout = async () => {
    // Keep pushSubscription rows on logout so Home Screen users are not wiped
    // by training account switches / accidental logouts. Endpoint is reassigned
    // to the next user on the same device via addPushSubscription.
    logout();
  }

  useEffect(() => {
    const endpoint = subscription?.endpoint;
    const p256dh = subscription?.keys?.p256dh;
    const auth = subscription?.keys?.auth;
    if (endpoint && p256dh && auth && user?._id && !localStorage.getItem("subscribing")) {
      addPushSubscription(endpoint, p256dh, auth, user._id);
    }
  }, [subscription, user?._id]);

  const loadNotification = async () => {
    try {
      const response = await client.query({
        query: notificationQuery,
        variables: {
          prospect_event_check_list_request: {
            filters: [
              {
                field: "receiver_id",
                operator: "=",
                value: String(user?._id ?? ""),
              }
            ]
          },
          crane_event_check_list_request: {
            filters: [
              {
                field: "receiver_id",
                operator: "=",
                value: String(user?._id ?? ""),
              }
            ]
          },
        },
        fetchPolicy: "network-only"
      });
      const prospect_notifications = response?.data?.prospect_event_check?.edges ?? [];
      const crane_notifications = response?.data?.crane_event_check?.edges ?? [];
      const result = [];
      prospect_notifications.map((item, index) => {
        result.push({ title: `Message from ${item.prospect_event.user_display_name}`, body: Number(item.prospect_event.type_id) === PROSPECT_EVENT_TYPE.IMAGE ? 'Image' : item.prospect_event.message, time: item.prospect_event.created_at, path: `/prospect/edit/${item.prospect_event.prospect_id}?sortBy=id&sortDirection=ASC&tab=Chat`, type: 'message', read_status: item.read_status });
      })
      crane_notifications.map((item, index) => {
        result.push({ title: `Message from ${item.crane_event.user_display_name}`, body: Number(item.crane_event.type_id) === CRANE_EVENT_TYPE.IMAGE ? 'Image' : item.crane_event.message, time: item.crane_event.created_at, path: `/ecrane/edit/${item.crane_event.crane_id}?sortBy=id&sortDirection=ASC&tab=Chat`, type: 'message', read_status: item.read_status });
      })
      result.sort((a, b) => new Date(b.time) - new Date(a.time));
      setNotifyCount(prospect_notifications.filter(item => item.read_status == 0).length + crane_notifications.filter(item => item.read_status == 0).length);
      setNotifications(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }

  const getTimeAgo = (time) => {
    const now = new Date();
    const past = new Date(time);
    const diffInSeconds = Math.floor((now - past) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
      second: 1
    };

    for (const [unit, seconds] of Object.entries(intervals)) {
      const count = Math.floor(diffInSeconds / seconds);
      if (count >= 1) {
        return `${count} ${unit}${count !== 1 ? "s" : ""} ago`;
      }
    }

    return "Just now";
  }

  const handleNotificationClick = (path) => {
    history.push(path);
    setIsNotificationOpen(false);
    setTimeout(() => {
      loadNotification();
    }, 500);
  };

  const markAllAsRead = async () => {
    try {
      const response = await client.mutate({
        mutation: eventCheckMutation,
        variables: {
          prospect_event_check_mutation_request: {
            id: 1,
            shouldDelete: true,
            properties: [
              { key: "prospect_id", number: 0 },
              { key: "receiver_id", string: user?._id },
              { key: "read_status", number: 1 },
            ],
          },
        },
      });
      loadNotification();
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (user?._id) loadNotification();
  }, [user?._id]);

  useEffect(() => {
    const broadcast = new BroadcastChannel("notification-channel");
    broadcast.onmessage = (event) => {
      if (event.data.data.url) {
        const url = new URL(event.data.data.url);
        const path = url.pathname.startsWith("/") ? url.pathname.substring(1) : url.pathname;
        const pathSegments = path.split("/");

        let channel = "";

        if (pathSegments.length >= 3) {
          if (pathSegments[1] === "edit") {
            if (pathSegments[0] === "ecrane") {
              channel = `ecrane-${pathSegments[2]}`;
            } else if (pathSegments[0] === "prospect") {
              channel = `prospect-${pathSegments[2]}`;
            }
          }
        }
        if (!localStorage.getItem('channel') || channel != localStorage.getItem('channel')) {
          loadNotification();
        }
      } else {
        loadNotification();
      }
    };
    return () => broadcast.close(); // Clean up when component unmounts
  }, [user?._id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target) &&
        bellRef.current && !bellRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
    };

    if (isNotificationOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNotificationOpen]);

  const [refetch, { loading, data }] = useLazyQuery(notificationQuery, {
    variables: {
      prospect_event_check_list_request: {
        filters: [
          {
            field: "receiver_id",
            operator: "=",
            value: String(user?._id ?? ""),
          }
        ]
      },
      crane_event_check_list_request: {
        filters: [
          {
            field: "receiver_id",
            operator: "=",
            value: String(user?._id ?? ""),
          }
        ]
      },
    },
    pollInterval: 5000,
    onCompleted: (localData) => {
      console.log('Success');
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const refreshNotification = async () => {
    setIsLoading(true);
    await loadNotification();
    setIsLoading(false);
  }

  const refreshPage = async () => {
    window.location.reload();
  }

  useEffect(() => {
    const prospect_notifications = data?.prospect_event_check?.edges ?? [];
    const crane_notifications = data?.crane_event_check?.edges ?? [];
    const result = [];
    prospect_notifications.map((item, index) => {
      result.push({ title: `Message from ${item.prospect_event.user_display_name}`, body: Number(item.prospect_event.type_id) === PROSPECT_EVENT_TYPE.IMAGE ? 'Image' : item.prospect_event.message, time: item.prospect_event.created_at, path: `/prospect/edit/${item.prospect_event.prospect_id}?sortBy=id&sortDirection=ASC&tab=Chat`, type: 'message', read_status: item.read_status });
    })
    crane_notifications.map((item, index) => {
      result.push({ title: `Message from ${item.crane_event.user_display_name}`, body: Number(item.crane_event.type_id) === CRANE_EVENT_TYPE.IMAGE ? 'Image' : item.crane_event.message, time: item.crane_event.created_at, path: `/ecrane/edit/${item.crane_event.crane_id}?sortBy=id&sortDirection=ASC&tab=Chat`, type: 'message', read_status: item.read_status });
    })
    result.sort((a, b) => new Date(b.time) - new Date(a.time));
    setNotifyCount(prospect_notifications.filter(item => item.read_status == 0).length + crane_notifications.filter(item => item.read_status == 0).length);
    setNotifications(result);
  }, [data])

  const allMenuItems = [
    {
      name: "Dashboard",
      icon: <FaHome />,
      path: "/dashboard",
      roles: ["administrator"],
    },
    {
      name: "Management",
      icon: <FaTrello />,
      path: "/dashboard/management",
      roles: ["administrator"],
    },
    {
      name: "Prospects",
      icon: <RiUserSearchFill />,
      path: "/prospect/list",
      roles: ["administrator"],
    },
    {
      name: "Customers",
      icon: <FaUsers />,
      path: "/customer/list",
      roles: ["administrator"],
    },
    {
      name: "Cranes",
      icon: <TbCrane />,
      path: "/ecrane/list"
    },
    {
      name: "Tickets",
      icon: <TbTicket />,
      path: "/ticket/list"
    },
    {
      name: "MapView",
      icon: <TbBrandGoogleMaps />,
      path: "/map-view",
      roles: ["administrator"],
    },
    {
      name: "Services",
      icon: <TbBellRinging2 />,
      path: "/services",
      roles: ["administrator", "technician"],
    },
    {
      name: "Parts",
      icon: <FaBoxes />,
      path: "/parts/list",
      roles: ["administrator", "technician"],
    },
    {
      name: "FSR",
      icon: <TbCrane />,
      path: "/fsr/list",
      roles: ["administrator"],
    },
    {
      name: "Settings",
      icon: <FaCog />,
      subItems: [
        {
          name: "Tags",
          icon: <TbTag />,
          path: "/tag/list",
          roles: ["administrator"],
        },
        {
          name: "Type Indicators",
          icon: <FaTrello />,
          path: "/type-indicator",
          roles: ["administrator"],
        },
        {
          name: "Location Indicators",
          icon: <FaTrello />,
          path: "/location-indicator",
          roles: ["administrator"],
        },
        {
          name: "Regions",
          icon: <FaTrello />,
          path: "/region/list",
          roles: ["administrator"],
        },
        {
          name: "Service Definitions",
          icon: <FaLayerGroup />,
          path: "/service-definitions",
          roles: ["administrator"],
        },
        {
          name: "Service Sections",
          icon: <FaCogs />,
          path: "/service-sections",
          roles: ["administrator"],
        },
        {
          name: "Service Items",
          icon: <TbCrane />,
          path: "/service-items",
          roles: ["administrator"],
        },
        {
          name: "Users",
          icon: <FaUsers />,
          path: "/user/list",
          roles: ["administrator"],
        },
        {
          name: "Notification Emails",
          icon: <FaEnvelope />,
          path: "/settings/notification-emails",
          roles: ["administrator"],
        },
        {
          name: "AI Connectors",
          icon: <FaRobot />,
          path: "/settings/mcp",
          roles: ["administrator"],
        },
      ],
      roles: ["administrator"],
    }
  ];

  if (authLoading) return <LoadingView />;
  if (!user) {
    return (
      <>
        <button
          type="button"
          className="theme-toggle-floating"
          onClick={ toggleTheme }
          title={ isDarkMode ? "Switch to light mode" : "Switch to dark mode" }
        >
          { isDarkMode ? "Light mode" : "Dark mode" }
        </button>
        <Switch>
          <Route path="/login" component={Login} />
          <Route path="/forgot-password" component={ForgotPassword} />
          <Route path="/reset-password" component={ResetPassword} />
          <Redirect to="/login" />
        </Switch>
      </>
    );
  }

  const processMenuItems = (items) => {
    return items
      .filter((item) => {
        if (item.roles && !hasAnyRole(user, item.roles)) {
          return false;
        }
        if (item.subItems) {
          const visibleSubItems = item.subItems.filter((subItem) => {
            if (!subItem.roles) return true;
            return hasAnyRole(user, subItem.roles);
          });
          return visibleSubItems.length > 0;
        }
        return true;
      })
      .map((item) => {
        const visibleSubItems = item.subItems
          ? item.subItems.filter((subItem) => {
              if (!subItem.roles) return true;
              return hasAnyRole(user, subItem.roles);
            })
          : null;

        if (visibleSubItems) {
          return {
            ...item,
            expanded: false,
            subItems: visibleSubItems.map((subItem) => ({
              ...subItem,
              onClick: () => {
                history.push(subItem.path);
                toggleMenu();
              },
              selected: false,
            })),
            selected: false,
          };
        }
        return {
          ...item,
          onClick: () => {
            history.push(item.path);
            toggleMenu();
          },
          selected: false,
        };
      });
  };

  const menuItems = processMenuItems(allMenuItems);
  const homePath = getDefaultRoute(user);

  return (
    <NotificationManager>
      {user.first_login && <FirstLoginWelcome onDismiss={dismissFirstLogin} />}
      <UserListProvider>
        <AppNavigation menuOpen={ menuOpen } toggleMenu={ toggleMenu }>
          <AppNavigation.Header pixelHeight={ 40 }>
            <BasicHeader
              title="EPerion | E-Crane"
              toggleMenu={ toggleMenu }
              menuOpen={ menuOpen }
            >
              <TbRefresh size={ 22 } style={ { cursor: "pointer" } } title="Refresh" onClick={ refreshPage } />
              <div style={ { flexGrow: 1 } }>
                <div style={ { float: "right", display: "flex", alignItems: "center", gap: "15px" } }>
                  <button
                    type="button"
                    className="theme-toggle-inline"
                    onClick={ toggleTheme }
                    title={ isDarkMode ? "Switch to light mode" : "Switch to dark mode" }
                  >
                    { isDarkMode ? "Light mode" : "Dark mode" }
                  </button>
                  <div
                    ref={ bellRef } // Attach ref to the wrapper, not the icon
                    style={ { cursor: "pointer", position: "relative", display: "flex", alignItems: "center" } }
                    onClick={ toggleNotification }
                  >
                    <TbBellRinging2 size={ 22 } title="Notifications" />
                    { notifyCount > 0 && <span className="badge"
                    >
                      { notifyCount > 99 ? "99+" : notifyCount }
                    </span> }
                  </div>
                  <span
                    style={ { cursor: "pointer" } }
                    onClick={ () => signout() }
                  >
                    Logout
                  </span>
                </div>
              </div>
            </BasicHeader>
          </AppNavigation.Header>
          <div ref={ notificationRef }>
            <AppNavigation.Notification isOpen={ isNotificationOpen } displayOption={ displayOption } optionChange={ handleOptionChange }>
              { isLoading ? <LoadingView /> : <>
                <div
                  style={ {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "12px",
                    marginBottom: "4px",
                  } }
                >
                  <span
                    className="link"
                    style={ { cursor: "pointer" } }
                    onClick={ () => {
                      setIsNotificationOpen(false);
                      history.push("/settings/push-notifications");
                    } }
                  >
                    Manage notifications
                  </span>
                  { notifications.length > 0 && (
                    <div style={ { cursor: "pointer" } } onClick={ markAllAsRead }>
                      Mark all as read
                    </div>
                  ) }
                </div>
                { notifications.map((item, index) => (
                  (item.read_status == 0 || displayOption == false) && <div
                    key={ index }
                    className="notification-item"
                    style={ item.type == "message" ? { cursor: "pointer" } : {} }
                    onClick={ item.type == "message" ? () => handleNotificationClick(item.path) : undefined }
                  >
                    <div style={ { display: "flex", justifyContent: "space-between" } }>
                      <div>{ item.title }</div>
                      <div>{ getTimeAgo(DateTime.fromISO(item.time, { zone: 'utc' }).toLocal()) }</div>
                    </div>
                    <div style={ {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      width: "100%",
                      display: "block"
                    } }
                    >
                      { item.body }
                    </div>
                  </div>
                )) }
                <div style={ { textAlign: "center", marginTop: notifications.length > 0 ? "10px" : "0px", height: "20px" } }>
                  <TbRefresh size={ 20 } title="Refresh" style={ { cursor: "pointer" } } onClick={ refreshNotification } />
                </div>
              </>
              }
            </AppNavigation.Notification>
          </div>
          <AppNavigation.MainMenu
            pixelWidth={ 300 }
            transition="transform 0.3s ease-in-out"
            style={ {
              zIndex: 1100,
            } }
          >
            <div
              style={ {
                display: "flex",
                gap: "10px",
                alignItems: "center",
                padding: "7px 6px",
              } }
            >
              <h4 style={ { margin: "0" } }>Main Menu</h4>
              <div>
                <button
                  onClick={ () => toggleMenu() }
                  style={ {
                    fontWeight: "bold",
                    padding: "4px 6px",
                  } }
                >
                  Close
                </button>
              </div>
            </div>
            <NavigationMenu
              toggleMenu={ toggleMenu }
              menuOpen={ menuOpen }
              items={ menuItems }
            />
          </AppNavigation.MainMenu>
          <AppNavigation.MainContent>
            <Switch>
              <Route path="/login" render={ () => <Redirect to={ homePath } /> } />
              <Route path="/forgot-password" render={ () => <Redirect to={ homePath } /> } />
              <Route path="/reset-password" render={ () => <Redirect to={ homePath } /> } />
              <Route
                exact
                path="/"
                render={ (props) => {
                  props.history.replace(homePath);
                } }
              />
              <Route
                exact
                path="/dashboard"
                render={ () => (
                  isTechnician(user) ? <Redirect to={ TECHNICIAN_HOME } /> : <Dashboard />
                ) }
              />
              <Route
                path="/dashboard/management"
                render={ () => (
                  isTechnician(user) ? <Redirect to={ TECHNICIAN_HOME } /> : <ManagementDashboard />
                ) }
              />
              <Route
                exact
                path="/my-cranes"
                render={ () => (
                  isCustomer(user) ? <CustomerHome /> : <Redirect to={ homePath } />
                ) }
              />
              <RouteStack path="/prospect">
                { (props) => (
                  isTechnician(user) ? (
                    <Redirect to={ TECHNICIAN_HOME } />
                  ) : (
                    <ProspectController
                      { ...props }
                      style={ { height: "calc(100vh - 40px)" } }
                    />
                  )
                ) }
              </RouteStack>
              <RouteStack path="/ecrane">
                { (props) => (
                  <CraneController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/tag">
                { (props) => (
                  <TagController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/ticket">
                { (props) => (
                  <TicketController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <Route
                path="/my-ticket"
                render={ ({ location }) => (
                  <Redirect to={ location.pathname.replace(/^\/my-ticket/, "/ticket") + location.search } />
                ) }
              />
              <Route
                path="/ecrane-view"
                render={ () => <Redirect to="/ecrane/list" /> }
              />
              <RouteStack path="/type-indicator">
                { (props) => (
                  <TypeIndicatorController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/location-indicator">
                { (props) => (
                  <LocationIndicatorController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/region">
                { (props) => (
                  <RegionController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/customer">
                { (props) => (
                  isAdministrator(user) ? (
                    <CustomerController
                      { ...props }
                      style={ { height: "calc(100vh - 40px)" } }
                    />
                  ) : (
                    <Redirect to={ homePath } />
                  )
                ) }
              </RouteStack>
              <RouteStack path="/map-view">
                { (props) => (
                  isTechnician(user) ? (
                    <Redirect to={ TECHNICIAN_HOME } />
                  ) : (
                    <MultiView
                      { ...props }
                      style={ { height: "calc(100vh - 40px)" } }
                    />
                  )
                ) }
              </RouteStack>
              <RouteStack path="/service-definitions">
                { (props) => (
                  <ServiceDefinitionController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/service-sections">
                { (props) => (
                  <ServiceSectionController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/service-types">
                { (props) => (
                  <ServiceTypeController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/service-units">
                { (props) => (
                  <ServiceUnitController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/service-groups">
                { (props) => (
                  <ServiceGroupController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/service-items">
                { (props) => (
                  <ServiceItemController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/services">
                { (props) => (
                  <ServiceController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/fsr">
                { (props) => (
                  isTechnician(user) ? (
                    <Redirect to={ TECHNICIAN_HOME } />
                  ) : (
                    <FSRController
                      { ...props }
                      style={ { height: "calc(100vh - 40px)" } }
                    />
                  )
                ) }
              </RouteStack>
              <RouteStack path="/parts">
                { (props) => (
                  <PartController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <RouteStack path="/user">
                { (props) => (
                  <UserController
                    { ...props }
                    style={ { height: "calc(100vh - 40px)" } }
                  />
                ) }
              </RouteStack>
              <Route
                exact
                path="/settings/notification-emails"
                render={ () => (
                  <NotificationEmailsSettings />
                ) }
              />
              <Route
                exact
                path="/settings/mcp"
                render={ () => (
                  isAdministrator(user) ? (
                    <McpSettings />
                  ) : (
                    <Redirect to={ homePath } />
                  )
                ) }
              />
              <Route
                exact
                path="/settings/push-notifications"
                render={ () => (
                  <PushNotificationsSettings />
                ) }
              />
            </Switch>
          </AppNavigation.MainContent>
        </AppNavigation>
      </UserListProvider>
    </NotificationManager>
  );
}

export default withRouter(App);
